import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import Layout from './components/layout/Layout';

// Lazy-loaded page components for code splitting
const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Contacts = lazy(() => import('./pages/Contacts'));
const ContactDetail = lazy(() => import('./pages/ContactDetail'));
const ContactForm = lazy(() => import('./pages/ContactForm'));
const Counties = lazy(() => import('./pages/Counties'));
const CountyDetail = lazy(() => import('./pages/CountyDetail'));
const CountyPortal = lazy(() => import('./pages/CountyPortal'));
const DistrictView = lazy(() => import('./pages/DistrictView'));
const Declarations = lazy(() => import('./pages/Declarations'));
const Donations = lazy(() => import('./pages/Donations'));
const DonationDetail = lazy(() => import('./pages/DonationDetail'));
const DonationReports = lazy(() => import('./pages/DonationReports'));
const Events = lazy(() => import('./pages/Events'));
const EventDetail = lazy(() => import('./pages/EventDetail'));
const EventCheckIn = lazy(() => import('./pages/EventCheckIn'));
const Import = lazy(() => import('./pages/Import'));
const Reports = lazy(() => import('./pages/Reports'));
const Settings = lazy(() => import('./pages/Settings'));
const AdminSettings = lazy(() => import('./pages/AdminSettings'));
const Users = lazy(() => import('./pages/Users'));
const TheCount = lazy(() => import('./pages/TheCount'));
const Duplicates = lazy(() => import('./pages/Duplicates'));
const Tags = lazy(() => import('./pages/Tags'));
const AuditLog = lazy(() => import('./pages/AuditLog'));
const MissionOrders = lazy(() => import('./pages/MissionOrders'));
const SupporterJourney = lazy(() => import('./pages/SupporterJourney'));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tnm-navy"></div></div>}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/the-count" element={<TheCount />} />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="contacts" element={<Contacts />} />
          <Route path="contacts/new" element={<ContactForm />} />
          <Route path="contacts/:id" element={<ContactDetail />} />
          <Route path="contacts/:id/edit" element={<ContactForm />} />
          <Route path="counties" element={<Counties />} />
          <Route path="counties/:name" element={<CountyDetail />} />
          <Route path="county/:id" element={<CountyPortal />} />
          <Route path="district/:id" element={<DistrictView />} />
          <Route path="mission-orders" element={<MissionOrders />} />
          <Route path="supporter-journey" element={<SupporterJourney />} />
          <Route path="declarations" element={<Declarations />} />
          <Route path="donations" element={<Donations />} />
          <Route path="donations/:id" element={<DonationDetail />} />
          <Route path="donation-reports" element={<DonationReports />} />
          <Route path="events" element={<Events />} />
          <Route path="events/:id" element={<EventDetail />} />
          <Route path="events/:id/check-in" element={<EventCheckIn />} />
          <Route path="import" element={<Import />} />
          <Route path="duplicates" element={<Duplicates />} />
          <Route path="tags" element={<Tags />} />
          <Route path="reports" element={<Reports />} />
          <Route path="settings" element={<Settings />} />
          <Route path="integrations" element={<AdminSettings />} />
          <Route path="audit-log" element={<AuditLog />} />
          <Route path="users" element={<Users />} />
        </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
